import { useEffect } from 'react'

/**
 * Custom hook for handling keyboard shortcuts
 * @param {Object} shortcuts - Object mapping key combinations to handlers
 * @param {Array} deps - Dependencies array for the effect
 *
 * Example usage:
 * useKeyboardShortcuts({
 *   'ctrl+n': () => console.log('New item'),
 *   'ctrl+s': () => console.log('Save'),
 *   '?': () => console.log('Show help')
 * })
 */
export const useKeyboardShortcuts = (shortcuts, deps = []) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't trigger shortcuts when typing in inputs, textareas, or contenteditable elements
      const target = event.target
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // Build the key combination string
      const keys = []
      if (event.ctrlKey || event.metaKey) keys.push('ctrl')
      if (event.altKey) keys.push('alt')
      if (event.shiftKey) keys.push('shift')

      const key = event.key.toLowerCase()
      if (!['control', 'alt', 'shift', 'meta'].includes(key)) {
        keys.push(key)
      }

      const combination = keys.join('+')

      // Check if we have a handler for this combination
      if (shortcuts[combination]) {
        event.preventDefault()
        shortcuts[combination](event)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, deps)
}

export default useKeyboardShortcuts
